import { IAttribute, IAttribute2 } from '../components/DTStudio/interfaces'
import {
    IChartAPIPayloadDataSet,
    ISelectedAttributes,
} from '../components/ViewEntity/interfaces'
import {
    AttributesMetadata,
    AttributeType,
    BUSINESS_RULES_FIELD_ID,
    EntityStatus,
    ShipmentAttributes,
    UserPermissions,
} from '../constants/constants'
import { RootState } from '../redux/store'
import {
    fetchEntityInstanceData,
    fetchRulesetsForProduct,
} from '../services/entityInstanceServices'
import { fetchResolvedOrgDataOrgId } from '../services/orgServices'
import {
    isBoolean,
    isCountryISOcode,
    isCustomAttribute,
    isDateTime,
    isDecimalType,
    isEmail,
    isFile,
    isMultiSelect,
    isNumberType,
    isRadio,
    isSelect,
    isStringType,
    isTimeSeries,
} from './AttributeUtils'
import {
    getDateTimeInISOFormate,
    getDateTimeUserFormate,
    getDateYYYYMMDDFormate,
} from './DateAndTimeUtils'
import { getCountryCodes } from './GetCountryCodes'
import dayjs from 'dayjs'

export const updateAttributeValues = (
    attributeData: any[],
    thunkAPI: { getState: () => unknown },
    isEdit: boolean = false,
): IAttribute2[] => {
    const state = thunkAPI.getState() as RootState

    const permissions = state.org.permissions
    const userPreferTimezone =
        state.user.user?.preferences?.localization?.timeZone
    const isSuperAdmin = permissions?.includes(UserPermissions.ORG_ADMIN)

    return attributeData?.map((item: any) => {
        const commonProps = {
            hide:
                item?.metadata?.Hidden ||
                (item?.metadata?.Superviewer && !isSuperAdmin),

            disabled: !!(
                item?.metadata?.[AttributesMetadata.VIEW_ONLY] && !isSuperAdmin
            ),
        }

        if (isSelect(item) || isRadio(item) || isMultiSelect(item)) {
            let listValues: any[] = []

            try {
                listValues = JSON.parse(item.range?.trim() || '[]')
            } catch {
                listValues = item.range?.split(',') || []
            }

            const updatedList = listValues
                ?.map((i: any) => {
                    if (i?.key !== undefined && i?.value !== undefined) {
                        return { id: i.key, name: i.value }
                    }
                    if (typeof i === 'object') {
                        return {
                            id: JSON.stringify(i),
                            name: JSON.stringify(i),
                        }
                    }
                    return { id: i, name: i }
                })
                ?.filter((i: any) => i.id || i.name)

            return {
                ...item,
                ...commonProps,
                list: updatedList,
                value: isMultiSelect(item)
                    ? item.value ?? []
                    : item.value ?? '',
            }
        }

        if (isBoolean(item)) {
            return {
                ...item,
                ...commonProps,
                value:
                    typeof item.value === 'boolean'
                        ? item.value
                        : item.defaultValue?.toLowerCase() === 'true',
            }
        }

        if (isDateTime(item)) {
            let updatedValue = item.value

            if (updatedValue?.datetime) {
                const timezone =
                    updatedValue.timezone ||
                    userPreferTimezone ||
                    dayjs.tz.guess()

                updatedValue = {
                    datetime: getDateTimeUserFormate(
                        updatedValue.datetime,
                        'MM/DD/YYYY HH:mm',
                        timezone,
                    ),
                    timezone,
                }
            }

            return {
                ...item,
                ...commonProps,
                value: updatedValue,
            }
        }

        if (isFile(item)) {
            let parsedValue = ''

            try {
                parsedValue = JSON.parse(item.value)
            } catch {
                parsedValue = item.value
            }

            return {
                ...item,
                ...commonProps,
                value: parsedValue,
            }
        }

        if (isEmail(item)) {
            const isArray = item.metadata?.Array

            const rawValue = item.value

            const normalizeArrayValue = () => {
                if (Array.isArray(rawValue)) return rawValue
                if (rawValue) return [rawValue]
                return []
            }

            const normalizeSingleValue = () => {
                if (Array.isArray(rawValue)) return rawValue[0]
                return rawValue ?? ''
            }

            const value = isArray
                ? normalizeArrayValue()
                : normalizeSingleValue()

            return {
                ...item,
                ...commonProps,
                value,
            }
        }

        if (isCustomAttribute(item)) {
            return {
                ...item,
                ...commonProps,
                hide: true,
            }
        }

        if (isCountryISOcode(item)) {
            return {
                ...item,
                ...commonProps,
                list: getCountryCodes(),
            }
        }
        if (isStringType(item) || isNumberType(item) || isDecimalType(item)) {
            return {
                ...item,
                ...commonProps,
                value: isEdit ? item.value ?? '' : item.defaultValue ?? '',
            }
        }

        return {
            ...item,
            ...commonProps,
            value: item.value ?? '',
        }
    })
}

export const updateValuesForPayload = (
    attributes: unknown[] | undefined,
): (IAttribute2 & { value: unknown })[] => {
    const updatedFields = (attributes || []).map((i: any) => {
        if (
            i.id.toLowerCase() === BUSINESS_RULES_FIELD_ID ||
            i.dataType.toLowerCase() === AttributeType.RULESET
        ) {
            let updatedValue = null
            if (Array.isArray(i.value)) {
                updatedValue = i.value?.map((i: any) => i.id)
            }
            return { ...i, value: updatedValue?.length ? updatedValue : null }
        } else if (i.dataType.toLowerCase() === AttributeType.DEVICE_RELATION) {
            const exsitingItems = new Map(
                i.selectedEntities?.map((e: any) => [e.entityId, e]),
            )
            const updatedValue = i.value
                ?.filter((d: any) => {
                    //if it's exsiting item. need to stop to send in API payload.
                    //if even exsting one and status is diffrent need to send to API
                    const exsitingItem: any = exsitingItems.get(d.entityId)
                    return !exsitingItem || exsitingItem.status !== d.status
                })
                ?.map((c: any) => {
                    return {
                        instanceId: c.entityId,
                        status: c.status || 'ACTIVE',
                    }
                })
            return { ...i, value: updatedValue }
        } else if (i.dataType.toLowerCase() === AttributeType.MULTI_SELECT) {
            return { ...i, value: i.value?.length ? i.value : null }
        } else if (i.dataType.toLowerCase() === AttributeType.DATE_TIME) {
            let updatedValue = null
            if (i.value?.datetime) {
                updatedValue = {
                    ...i.value,
                    datetime: getDateTimeInISOFormate(
                        i.value?.datetime,
                        i.value?.timezone,
                        'MM/DD/YYYY HH:mm',
                    ),
                }
            }
            return { ...i, value: updatedValue }
        } else if (i.dataType.toLowerCase() === AttributeType.DATE) {
            let updatedValue = null
            if (i.value !== '' && i.value !== null) {
                updatedValue = getDateYYYYMMDDFormate(i.value)
            }
            return { ...i, value: updatedValue }
        } else if (i.dataType.toLowerCase() === AttributeType.FILE) {
            let updatedValue = null
            if (i.value !== '' && i.value !== null) {
                updatedValue = JSON.stringify(i.value)
            }
            return { ...i, value: updatedValue }
        } else if (
            i.dataType.toLowerCase() === AttributeType.NUMBER ||
            i.dataType.toLowerCase() === AttributeType.DECIMAL ||
            i.dataType.toLowerCase() === AttributeType.THRESHOLD
        ) {
            return {
                ...i,
                value:
                    i.value !== null && i.value !== undefined && i.value !== ''
                        ? Number(i.value)
                        : null,
            }
        } else if (
            i.dataType.toLowerCase() === AttributeType.TEMPERATURE ||
            i.dataType.toLowerCase() === AttributeType.HUMIDITY ||
            i.dataType.toLowerCase() === AttributeType.WEIGHT ||
            i.dataType.toLowerCase() === AttributeType.DISTANCE ||
            i.dataType.toLowerCase() === AttributeType.CURRENCY
        ) {
            let updatedVlaue = null
            if (i.value !== null && i.value !== undefined) {
                updatedVlaue = {
                    value: i.value !== '' ? Number(i.value) : null,
                    units: i.unit ?? '',
                }
            }
            return { ...i, value: updatedVlaue }
        } else if (i.dataType.toLowerCase() === AttributeType.SEALINE) {
            return { ...i, value: i.value ? i.value.id : null }
        } else if (i.dataType.toLowerCase() === AttributeType.JSON) {
            const updatedValue = transformJSONValueForUpdateAPIPayload(i)
            return { ...i, value: normalizeValue(updatedValue) }
        } else {
            return {
                ...i,
                value: normalizeValue(i.value),
            }
        }
    })
    return updatedFields
}

export const transformJSONValueForUpdateAPIPayload = (
    attribute: IAttribute,
): any => {
    let updatedValue = attribute.value
    if (Array.isArray(attribute.value)) {
        updatedValue = attribute.value.map((item: any) => {
            const updatedItem: any = {}

            for (const key in item) {
                const val = item[key]
                // Check if nested value is of datatype Entity
                if (
                    val &&
                    typeof val === 'object' &&
                    val.dataType &&
                    val.dataType?.toLowerCase() === AttributeType.ENTITY
                ) {
                    updatedItem[key] = val.entityId
                } else {
                    updatedItem[key] = val
                }
            }

            return updatedItem
        })
    } else if (
        typeof attribute.value === 'object' &&
        attribute.value !== null
    ) {
        const updatedObj: any = {}

        for (const key in attribute.value) {
            const val = attribute.value[key]

            if (
                val &&
                typeof val === 'object' &&
                val.dataType &&
                val.dataType.toLowerCase() === AttributeType.ENTITY
            ) {
                updatedObj[key] = val.entityId
            } else {
                updatedObj[key] = val
            }
        }

        updatedValue = updatedObj
    }
    return updatedValue
}

export const isValidEmail = (emailAddress: string): boolean => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
    return emailRegex.test(emailAddress.toLowerCase())
}

export const getChartAPIPayloadDataSets = (
    selectedAttributes: ISelectedAttributes[],
    filterOutNonTimeSeriesData = true,
): IChartAPIPayloadDataSet[] => {
    return selectedAttributes
        .filter((item) => {
            if (filterOutNonTimeSeriesData) {
                return isTimeSeries(item.attribute)
            } else {
                return true
            }
        })
        .map((item) => {
            return {
                entityInstanceId: item.entityInstanceId,
                attributeId: item.attribute.id,
                dataType: item.attribute.dataType,
            }
        })
}

export const getAttributeForChartDataSet = (
    selectedAttributes: ISelectedAttributes[],
    attributeId: string,
    entityInstanceId: string,
): IAttribute | undefined => {
    return selectedAttributes.find(
        (item) =>
            item.attribute.id === attributeId &&
            item.entityInstanceId === entityInstanceId,
    )?.attribute
}

export const normalizeValue = (value: unknown): any => {
    if (
        value === undefined ||
        value === '' ||
        (Array.isArray(value) && value.length === 0) ||
        (typeof value === 'object' &&
            value !== null &&
            Object.keys(value).length === 0)
    ) {
        return null
    }
    return value
}
export const setDefaultValues = async (
    attributes: IAttribute[],
    orgId: string,
): Promise<{
    updatedAttributes: IAttribute[]
    productRulesets: { id: string; name: string }[]
}> => {
    const hasProductAttribute = attributes?.find(
        (i) => i.id === ShipmentAttributes.ProductAttributeId,
    )
    const hasOriginAttribute = attributes?.find(
        (i) => i.id === ShipmentAttributes.OriginLocationAttributeId,
    )
    const hasDestinationAttribute = attributes?.find(
        (i) => i.id === ShipmentAttributes.DestinationLocationAttributeId,
    )

    const entityType: string[] = []
    if (hasProductAttribute) {
        entityType.push(ShipmentAttributes.ProductAttributeId)
    }
    if (hasOriginAttribute) {
        entityType.push(ShipmentAttributes.OriginLocationAttributeId)
    }
    if (hasDestinationAttribute) {
        entityType.push(ShipmentAttributes.DestinationLocationAttributeId)
    }

    if (entityType.length === 0) {
        return { updatedAttributes: attributes, productRulesets: [] }
    }

    const selectedOrgData = await fetchResolvedOrgDataOrgId(
        orgId,
        entityType.join(','),
        'entityType',
    )
    let defaultProduct = selectedOrgData?.preferences?.defaultProduct || null
    let defaultShipper = selectedOrgData?.preferences?.defaultShipper || null
    let defaultReceiver = selectedOrgData?.preferences?.defaultReceiver || null

    const validationPromises = []

    if (defaultProduct?.entityId && defaultProduct?.templateId) {
        validationPromises.push(
            fetchEntityInstanceData(
                orgId,
                defaultProduct.templateId,
                defaultProduct.entityId,
            ).then((response) => ({ type: 'product', response })),
        )
    }

    if (defaultShipper?.entityId && defaultShipper?.templateId) {
        validationPromises.push(
            fetchEntityInstanceData(
                orgId,
                defaultShipper.templateId,
                defaultShipper.entityId,
            ).then((response) => ({ type: 'shipper', response })),
        )
    }

    if (defaultReceiver?.entityId && defaultReceiver?.templateId) {
        validationPromises.push(
            fetchEntityInstanceData(
                orgId,
                defaultReceiver.templateId,
                defaultReceiver.entityId,
            ).then((response) => ({ type: 'receiver', response })),
        )
    }

    const validationResults = await Promise.all(validationPromises)

    // Share List carried by the resolved default receiver. Mirrors the manual
    // dropdown-selection path in EntityTypeAttribute, which auto-populates the
    // shipment's Share List from the selected receiver's shareList attribute.
    let defaultReceiverShareList: unknown[] | null = null

    validationResults.forEach(({ type, response }) => {
        if (type === 'product' && defaultProduct) {
            if (response?.status === EntityStatus.DELETE) {
                defaultProduct = null
            } else {
                defaultProduct.uid = response?.uid || defaultProduct.uid
            }
        } else if (type === 'shipper' && defaultShipper) {
            if (response?.status === EntityStatus.DELETE) {
                defaultShipper = null
            } else {
                defaultShipper.uid = response?.uid || defaultShipper.uid
            }
        } else if (type === 'receiver' && defaultReceiver) {
            if (response?.status === EntityStatus.DELETE) {
                defaultReceiver = null
            } else {
                defaultReceiver.uid = response?.uid || defaultReceiver.uid
                const shareList = response?.attributes?.find(
                    (a: IAttribute) =>
                        a.id === ShipmentAttributes.ShareListAttributeId,
                )?.value
                if (Array.isArray(shareList) && shareList.length > 0) {
                    defaultReceiverShareList = shareList
                }
            }
        }
    })

    let productRulesets: { id: string; name: string }[] = []

    const updatedAttributes = attributes?.map((attr) => {
        if (attr.id === ShipmentAttributes.ProductAttributeId) {
            return { ...attr, value: defaultProduct }
        }
        if (attr.id === ShipmentAttributes.OriginLocationAttributeId) {
            return { ...attr, value: defaultShipper }
        }
        if (attr.id === ShipmentAttributes.DestinationLocationAttributeId) {
            return { ...attr, value: defaultReceiver }
        }
        // Auto-populate the shipment's Share List from the default receiver
        // (only when the receiver was resolved and carries a non-empty list).
        if (
            attr.id === ShipmentAttributes.ShareListAttributeId &&
            defaultReceiver &&
            defaultReceiverShareList
        ) {
            return { ...attr, value: defaultReceiverShareList }
        }
        return attr
    })

    if (hasProductAttribute && defaultProduct?.entityId) {
        const response = await fetchRulesetsForProduct(
            orgId,
            defaultProduct.entityId,
        )
        productRulesets = response
    }

    return { updatedAttributes, productRulesets }
}
